// src/App.tsx
import { Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { useAnalytics } from './hooks/useAnalytics';
import { useMediaSecurity } from './hooks/useMediaSecurity';
import RedirectManager from './utils/redirectManager';
import HomePage from './pages/Home/Homepage/HomePage';
import OriginalsPage from './pages/Originals/OriginalsPage';
import MediaPage from './pages/Media/MediaPage';
import CinemaPage from './pages/Media/CinemaPage';
import ContentsPage from './pages/Media/ContentsPage';
import PodcastsPage from './pages/Podcasts/PodcastsPage';
import AnimationsPage from './pages/Animations/AnimationsPage';
import SneakPeeksPage from './pages/SneakPeeks/SneakPeeksPage';
import LoginPage from './pages/Auth/LoginPage';
import SignupPage from './pages/Auth/SignupPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';
import ProfilesPage from './pages/Profiles/ProfilesPage';
import AdminLayout from './components/Admin/AdminLayout';

import { useAuthInit } from './hooks/useAuthInit';
import ToastContainer from './components/Common/ToastContainer';
import CookieConsent from './components/Common/CookieConsent';

// Import the actual implemented user pages instead of placeholders
import ProfilePage from './pages/User/ProfilePage';
import LibraryPage from './pages/User/LibraryPage';
import FavoritesPage from './pages/User/FavoritesPage';
import SettingsPage from './pages/User/SettingsPage';


// Import the new unified player
import UnifiedPlayerPage from './pages/UnifiedPlayer/UnifiedPlayerPage';

// Legal and informational pages
import AboutPage from './pages/Legal/AboutPage';
import ContactPage from './pages/Legal/ContactPage';
import FAQPage from './pages/Legal/FAQPage';
import PrivacyPolicyPage from './pages/Legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/Legal/TermsOfServicePage';
import CookiePolicyPage from './pages/Legal/CookiePolicyPage';

// Import live video watch page
import LiveVideoWatchPage from './pages/LiveVideo/LiveVideoWatchPage';

// Import live streams pages
import LiveStreamsPage from './pages/LiveStreams/LiveStreamsPage';
import LiveStreamWatchPage from './pages/LiveStreams/LiveStreamWatchPage';

// Import 404 page
import NotFoundPage from './pages/NotFound/NotFoundPage';

// Import newsletter pages
import NewsletterVerifyPage from './pages/Newsletter/NewsletterVerifyPage';
import NewsletterUnsubscribePage from './pages/Newsletter/NewsletterUnsubscribePage';

// StrymHub Studios — the production company side of the platform
import StudiosPage from './pages/Studios/StudiosPage';
import ProductionsPage from './pages/Productions/ProductionsPage';
import ProductionDetailPage from './pages/Productions/ProductionDetailPage';
import PartnersPage from './pages/Partners/PartnersPage';

// StrymHub Community — the repertory we cast and crew from
import CommunityPage from './pages/Community/CommunityPage';
import TroupePage from './pages/Community/TroupePage';
import MemberProfilePage from './pages/Community/MemberProfilePage';
import JoinTroupePage from './pages/Community/JoinTroupePage';
import CastingPage from './pages/Casting/CastingPage';
import CastingCallDetailPage from './pages/Casting/CastingCallDetailPage';
import MyApplicationsPage from './pages/Casting/MyApplicationsPage';

// Placeholder components for admin pages not yet implemented

function App() {
  useAuthInit();
  useMediaSecurity(); // Initialize global media security
  
  // Initialize Google Analytics page tracking
  useAnalytics();

  useEffect(() => {
    // Handle SEO canonical redirects first (only in production)
    if (import.meta.env.VITE_NODE_ENV === 'production') {
      RedirectManager.handleCanonicalRedirects();
    }
    
    const originalClear = sessionStorage.clear;
    const originalRemove = sessionStorage.removeItem;
  
  sessionStorage.clear = function() {
    return originalClear.call(this);
  };
  
  sessionStorage.removeItem = function(key) {
    return originalRemove.call(this, key);
  };
  
  // Also monitor localStorage
  const originalLocalClear = localStorage.clear;
  const originalLocalRemove = localStorage.removeItem;
  
  localStorage.clear = function() {
    return originalLocalClear.call(this);
  };
  
  localStorage.removeItem = function(key) {
    return originalLocalRemove.call(this, key);
  };

  return () => {
    sessionStorage.clear = originalClear;
    sessionStorage.removeItem = originalRemove;
    localStorage.clear = originalLocalClear;
    localStorage.removeItem = originalLocalRemove;
  };
}, []);

  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/originals" element={<OriginalsPage />} />
        <Route path="/media" element={<MediaPage />} />
        <Route path="/media/cinema" element={<CinemaPage />} />
        <Route path="/media/contents" element={<ContentsPage />} />
        
        {/* Media player routes - public with 90-sec preview for unauthenticated users */}
        <Route path="/media/cinema/:id" element={<UnifiedPlayerPage />} />
        <Route path="/media/contents/:id" element={<UnifiedPlayerPage />} />
        
        <Route path="/podcasts" element={<PodcastsPage />} />
        <Route path="/animations" element={<AnimationsPage />} />
        <Route path="/sneak-peeks" element={<SneakPeeksPage />} />

        {/* StrymHub Studios — the production company */}
        <Route path="/studios" element={<StudiosPage />} />
        <Route path="/productions" element={<ProductionsPage />} />
        <Route path="/productions/:slug" element={<ProductionDetailPage />} />
        <Route path="/partners" element={<PartnersPage />} />

        {/* StrymHub Community — the repertory */}
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/community/troupe" element={<TroupePage />} />
        <Route path="/community/troupe/:slug" element={<MemberProfilePage />} />
        <Route path="/community/join" element={
          <ProtectedRoute>
            <JoinTroupePage />
          </ProtectedRoute>
        } />

        {/* Casting — open calls are public, applications need an account */}
        <Route path="/casting" element={<CastingPage />} />
        <Route path="/casting/my-applications" element={
          <ProtectedRoute>
            <MyApplicationsPage />
          </ProtectedRoute>
        } />
        <Route path="/casting/:slug" element={<CastingCallDetailPage />} />
        
        {/* Live Streams Routes - all public, chat/reactions gated in-page */}
        <Route path="/live" element={<LiveStreamsPage />} />
        <Route path="/live/:slug" element={<LiveStreamWatchPage />} />

        {/* Legal and Informational Pages */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        
        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/register" element={<SignupPage />} /> {/* Alias for signup */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        
        {/* Newsletter Pages */}
        <Route path="/newsletter/verify" element={<NewsletterVerifyPage />} />
        <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        {/* Who is watching. Behind ProtectedRoute: these belong to the account holder,
            and the server scopes every lookup to the account behind the token. */}
        <Route path="/profiles" element={<ProtectedRoute><ProfilesPage /></ProtectedRoute>} />

        {/* Admin Routes - Protected by AdminRoute */}
        <Route path="/admin/*" element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        } />
        
        {/* Content Routes - Using Unified Player - public with 90-sec preview */}
        <Route path="/watch/:type/:id" element={<UnifiedPlayerPage />} />
        
        {/* Legacy live video route - redirects to old VOD "live" videos */}
        <Route path="/live-video/:slug" element={<LiveVideoWatchPage />} />
        
        {/* Legacy routes redirecting to unified player - public with preview */}
        <Route path="/podcast/:id" element={<UnifiedPlayerPage />} />
        <Route path="/podcasts/:id" element={<UnifiedPlayerPage />} />
        <Route path="/animation/:id" element={<UnifiedPlayerPage />} />
        <Route path="/animations/:id" element={<UnifiedPlayerPage />} />
        <Route path="/sneak-peek/:slug" element={<UnifiedPlayerPage />} />
        <Route path="/sneak-peeks/:id" element={<UnifiedPlayerPage />} />
        
        {/* New unified routes - public with preview */}
        <Route path="/cinema/:id" element={<UnifiedPlayerPage />} />
        <Route path="/content/:id" element={<UnifiedPlayerPage />} />
        <Route path="/series/:id" element={<UnifiedPlayerPage />} />
        
        {/* Protected User Routes - These are now fully implemented! */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/library" element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        } />
        <Route path="/favorites" element={
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        {/* Catch-all route for 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <ToastContainer />
      <CookieConsent />
    </div>
  );
}

export default App;